import {useCallback, useRef} from 'react'

import type {ConnectionOperation, EntityOperation} from '@github-ui/optimistic-connection'
import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  ENTITY_FIELDS_OWNER,
  entityFieldsMutationKey,
  type ConnectionMutationVariables,
} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useMutation} from '@github-ui/react-query'

import {
  buildSubIssueNodeFromServer,
  buildSubIssueOptimisticNode,
  type SubIssueOptimisticNodeInput,
} from './sub-issue-optimistic-node'
import {
  type AddSubIssuePayload,
  addSubIssueRequest,
  type RemoveSubIssuePayload,
  removeSubIssueRequest,
} from './sub-issue-membership-api'
import {
  buildAddSubIssueOperation,
  buildConfirmedSubIssuesSummaryOperation,
  buildRemoveSubIssueOperation,
  buildSubIssueParentOperation,
  SUB_ISSUES_CONNECTION_PATH,
  type SubIssueParentData,
  subIssuesMutationScopeId,
} from './sub-issue-membership-operations'
export type MembershipCoordinates = {
  owner?: string
  repo?: string
  number?: number
  parentIssueId: string
}

type ValidMembershipCoordinates = Required<MembershipCoordinates>
type AddMembershipCoordinates = MembershipCoordinates & {parent: SubIssueParentData}

type LinkedEntityMutationVariables<TPayload> = ConnectionMutationVariables & {
  operation: EntityOperation
  settled: Promise<TPayload>
}
type AddLinkedEntityMutationVariables = LinkedEntityMutationVariables<AddSubIssuePayload> & {
  parent: SubIssueParentData
}

type Deferred<T> = {
  promise: Promise<T>
  resolve: (value: T) => void
  reject: (reason: unknown) => void
}

type PendingMutation<TPayload> = {
  server: Deferred<TPayload>
  completion?: Promise<TPayload>
  turn: SubIssueMutationTurn
}

type SubIssueMutationTurn = {
  waitForPrevious: Promise<void>
  release: () => void
}

const subIssueMutationTails = new Map<string, Promise<void>>()

function deferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void
  let reject!: (reason: unknown) => void
  const promise = new Promise<T>((onResolve, onReject) => {
    resolve = onResolve
    reject = onReject
  })
  return {promise, resolve, reject}
}

function validCoordinates(coordinates: MembershipCoordinates): ValidMembershipCoordinates {
  if (
    !coordinates.owner ||
    !coordinates.repo ||
    !Number.isInteger(coordinates.number) ||
    (coordinates.number ?? 0) <= 0
  ) {
    throw new Error('A valid parent repository and issue number are required to update sub-issue membership')
  }
  return coordinates as ValidMembershipCoordinates
}

function createSubIssueMutationTurn(subIssueId: string): SubIssueMutationTurn {
  const waitForPrevious = subIssueMutationTails.get(subIssueId) ?? Promise.resolve()
  const current = deferred<void>()
  subIssueMutationTails.set(subIssueId, current.promise)

  return {
    waitForPrevious,
    release: () => {
      current.resolve()
      if (subIssueMutationTails.get(subIssueId) === current.promise) {
        subIssueMutationTails.delete(subIssueId)
      }
    },
  }
}

async function awaitMutationCompletion<TPayload>(
  mutationPromises: [Promise<TPayload>, Promise<unknown>],
  onSettled: () => void,
): Promise<TPayload> {
  try {
    const [payload, linkedEntity] = await Promise.allSettled(mutationPromises)
    if (payload.status === 'rejected') throw payload.reason
    if (linkedEntity.status === 'rejected') throw linkedEntity.reason
    return payload.value
  } finally {
    onSettled()
  }
}

function assertAddOperation(
  operation: ConnectionOperation,
): asserts operation is Extract<ConnectionOperation, {type: 'addToConnection'}> {
  if (operation.type !== 'addToConnection') throw new Error(`Unexpected ${operation.type} sub-issue operation`)
}

function assertRemoveOperation(
  operation: ConnectionOperation,
): asserts operation is Extract<ConnectionOperation, {type: 'removeFromConnection'}> {
  if (operation.type !== 'removeFromConnection') throw new Error(`Unexpected ${operation.type} sub-issue operation`)
}

export function useAddSubIssueMutation(coordinates: AddMembershipCoordinates) {
  const {owner, repo, number, parent, parentIssueId} = coordinates
  const pendingBySubIssueIdRef = useRef(new Map<string, PendingMutation<AddSubIssuePayload>>())

  const connectionMutation = useConnectionMutation({
    ownerId: parentIssueId,
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    scope: {id: subIssuesMutationScopeId(parentIssueId)},
    mutationFn: async operation => {
      assertAddOperation(operation)
      const pending = pendingBySubIssueIdRef.current.get(operation.node.id)
      if (!pending) throw new Error('Missing pending add sub-issue request')

      await pending.turn.waitForPrevious
      try {
        const valid = validCoordinates({owner, repo, number, parentIssueId})
        const payload = await addSubIssueRequest({
          owner: valid.owner,
          repo: valid.repo,
          number: valid.number,
          input: {subIssueId: operation.node.id, replaceParent: true},
        })
        pending.server.resolve(payload)
      } catch (error) {
        pending.server.reject(error)
      }
      return pending.server.promise
    },
    onSuccess: async (payload, variables) => {
      const operationId = variables.operationId
      await notifySettlement(
        buildAddSubIssueOperation(buildSubIssueNodeFromServer(payload.subIssue)),
        parentIssueId,
        SUB_ISSUES_CONNECTION_PATH,
        operationId,
      )
      await notifySettlement(
        buildConfirmedSubIssuesSummaryOperation(parentIssueId, payload.issue.subIssuesSummary),
        parentIssueId,
        DOCUMENT_FIELDS_PATH,
        operationId ? `${operationId}:summary` : undefined,
      )
    },
  })

  const parentMutation = useMutation<AddSubIssuePayload, Error, AddLinkedEntityMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables => variables.settled,
    onSuccess: async (payload, variables) => {
      await notifySettlement(
        buildSubIssueParentOperation(payload.subIssue.id, {
          ...variables.parent,
          id: payload.issue.id,
          subIssuesSummary: payload.issue.subIssuesSummary,
        }),
        ENTITY_FIELDS_OWNER,
        DOCUMENT_FIELDS_PATH,
        variables.operationId,
      )
    },
  })
  const {mutateAsync: mutateConnectionAsync} = connectionMutation
  const {mutateAsync: mutateParentAsync} = parentMutation

  const addSubIssue = useCallback(
    (subIssue: SubIssueOptimisticNodeInput) => {
      validCoordinates({owner, repo, number, parentIssueId})
      const existing = pendingBySubIssueIdRef.current.get(subIssue.id)?.completion
      if (existing) return existing

      const operationId = crypto.randomUUID()
      const pending: PendingMutation<AddSubIssuePayload> = {
        server: deferred(),
        turn: createSubIssueMutationTurn(subIssue.id),
      }
      pendingBySubIssueIdRef.current.set(subIssue.id, pending)
      const completion = awaitMutationCompletion(
        [
          mutateConnectionAsync({
            operation: buildAddSubIssueOperation(buildSubIssueOptimisticNode(subIssue)),
            operationId: `${operationId}:connection`,
          }),
          mutateParentAsync({
            operation: buildSubIssueParentOperation(subIssue.id, parent),
            operationId: `${operationId}:parent`,
            parent,
            settled: pending.server.promise,
          }),
        ],
        () => {
          pendingBySubIssueIdRef.current.delete(subIssue.id)
          pending.turn.release()
        },
      )
      pending.completion = completion
      return completion
    },
    [mutateConnectionAsync, mutateParentAsync, number, owner, parent, parentIssueId, repo],
  )

  return {...connectionMutation, addSubIssue}
}

export function useRemoveSubIssueMutation(coordinates: MembershipCoordinates, _legacyRelayEnvironment?: object) {
  const {owner, repo, number, parentIssueId} = coordinates
  const pendingBySubIssueIdRef = useRef(new Map<string, PendingMutation<RemoveSubIssuePayload>>())

  const connectionMutation = useConnectionMutation({
    ownerId: parentIssueId,
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    scope: {id: subIssuesMutationScopeId(parentIssueId)},
    mutationFn: async operation => {
      assertRemoveOperation(operation)
      const pending = pendingBySubIssueIdRef.current.get(operation.nodeId)
      if (!pending) throw new Error('Missing pending remove sub-issue request')

      await pending.turn.waitForPrevious
      try {
        const valid = validCoordinates({owner, repo, number, parentIssueId})
        const payload = await removeSubIssueRequest({
          owner: valid.owner,
          repo: valid.repo,
          number: valid.number,
          input: {subIssueId: operation.nodeId},
        })
        pending.server.resolve(payload)
      } catch (error) {
        pending.server.reject(error)
      }
      return pending.server.promise
    },
    onSuccess: async (payload, variables) => {
      const operationId = variables.operationId
      const optimisticOperation = variables.operation
      assertRemoveOperation(optimisticOperation)
      const closed =
        optimisticOperation.counts?.some(
          count => count.delta === -1 && count.path.join('.') === 'subIssuesSummary.completed',
        ) ?? false
      await notifySettlement(
        buildRemoveSubIssueOperation(payload.subIssue.id, closed),
        parentIssueId,
        SUB_ISSUES_CONNECTION_PATH,
        operationId,
      )
      await notifySettlement(
        buildConfirmedSubIssuesSummaryOperation(parentIssueId, payload.issue.subIssuesSummary),
        parentIssueId,
        DOCUMENT_FIELDS_PATH,
        operationId ? `${operationId}:summary` : undefined,
      )
    },
  })

  const parentMutation = useMutation<
    RemoveSubIssuePayload,
    Error,
    LinkedEntityMutationVariables<RemoveSubIssuePayload>
  >({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables => variables.settled,
    onSuccess: async (payload, variables) => {
      await notifySettlement(
        buildSubIssueParentOperation(payload.subIssue.id, payload.subIssue.parent),
        ENTITY_FIELDS_OWNER,
        DOCUMENT_FIELDS_PATH,
        variables.operationId,
      )
    },
  })
  const {mutateAsync: mutateConnectionAsync} = connectionMutation
  const {mutateAsync: mutateParentAsync} = parentMutation

  const removeSubIssue = useCallback(
    ({subIssueId, closed}: {subIssueId: string; closed: boolean}) => {
      validCoordinates({owner, repo, number, parentIssueId})
      const existing = pendingBySubIssueIdRef.current.get(subIssueId)?.completion
      if (existing) return existing

      const operationId = crypto.randomUUID()
      const pending: PendingMutation<RemoveSubIssuePayload> = {
        server: deferred(),
        turn: createSubIssueMutationTurn(subIssueId),
      }
      pendingBySubIssueIdRef.current.set(subIssueId, pending)
      const completion = awaitMutationCompletion(
        [
          mutateConnectionAsync({
            operation: buildRemoveSubIssueOperation(subIssueId, closed),
            operationId: `${operationId}:connection`,
          }),
          mutateParentAsync({
            operation: buildSubIssueParentOperation(subIssueId, null),
            operationId: `${operationId}:parent`,
            settled: pending.server.promise,
          }),
        ],
        () => {
          pendingBySubIssueIdRef.current.delete(subIssueId)
          pending.turn.release()
        },
      )
      pending.completion = completion
      return completion
    },
    [mutateConnectionAsync, mutateParentAsync, number, owner, parentIssueId, repo],
  )

  return {...connectionMutation, removeSubIssue}
}
